import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaCalendarAlt,
  FaFilter,
  FaFileAlt,
  FaDownload,
  FaFileImport,
  FaGlobe,
  FaMagic,
  FaPlus,
  FaRegEdit,
  FaSearch,
  FaTrashAlt,
  FaCopy,
  FaClone,
  FaQrcode,
  FaAddressBook,
} from 'react-icons/fa';
import api from '../api';
import FormBuilder from './FormBuilder';
import CustomerFormImport from '../components/CustomerFormImport';
import Modal from '../components/Modal';
import QrCodeModal from '../components/QrCodeModal';
import { useAuth } from '../AuthContext';
import {
  hasPermission,
  isAdmin,
  FORM_CREATE_PERMISSION,
  FORM_UPDATE_PERMISSION,
  FORM_DELETE_PERMISSION,
} from '../utils/permissions';
import FormAiDocumentAttachment from '../components/FormAiDocumentAttachment';
import useFormAiDocument from '../hooks/useFormAiDocument';
import { buildFormAiRequest, normalizeFormAiSuggestion } from '../utils/formAiDocument';
import { FORM_ASSISTANT_GUIDES } from '../data/formAssistantGuides';

const parseDate = (value, endOfDay = false) => {
  if (!value) return null;
  const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const FORM_ACTION_BUTTON_BASE =
  'group inline-flex h-9 min-w-[2.25rem] max-w-[2.25rem] shrink-0 items-center justify-center gap-0 overflow-hidden rounded-xl border p-0 text-sm font-medium transition-[max-width,padding,gap,background-color,border-color] duration-200 ease-out hover:max-w-[9rem] hover:gap-2 hover:px-3 focus-visible:max-w-[9rem] focus-visible:gap-2 focus-visible:px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200';

function FormActionButton({ icon: Icon, label, onClick, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-form-action={label.toLowerCase()}
      className={`${FORM_ACTION_BUTTON_BASE} ${className}`}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-200 group-hover:max-w-[6rem] group-hover:opacity-100 group-focus-visible:max-w-[6rem] group-focus-visible:opacity-100">
        {label}
      </span>
    </button>
  );
}

export default function Forms({ openTab, assistantAction = null }) {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [copiedFormId, setCopiedFormId] = useState(null);
  const [qrForm, setQrForm] = useState(null);
  const aiDocument = useFormAiDocument();
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState('');
  const [showPromptSuggestions, setShowPromptSuggestions] = useState(false);
  const [selectedGeneratedFieldIndexes, setSelectedGeneratedFieldIndexes] = useState([]);
  const [expandedExternalLinks, setExpandedExternalLinks] = useState({});
  const [importingForm, setImportingForm] = useState(false);
  const [customerImportOpen, setCustomerImportOpen] = useState(false);
  const { user } = useAuth();
  const canCreate = isAdmin(user) || hasPermission(user, FORM_CREATE_PERMISSION);
  const canEdit = isAdmin(user) || hasPermission(user, FORM_UPDATE_PERMISSION);
  const canDelete = isAdmin(user) || hasPermission(user, FORM_DELETE_PERMISSION);
  const quickPromptSuggestions = useMemo(
    () =>
      FORM_ASSISTANT_GUIDES.flatMap((guide) =>
        Array.isArray(guide.promptExamples) ? guide.promptExamples : [],
      ),
    [],
  );

  const applyPromptSuggestion = (suggestion) => {
    setAiPrompt((current) => {
      const trimmed = current.trim();
      if (!trimmed) {
        return suggestion;
      }
      if (trimmed.endsWith('.') || trimmed.endsWith('?')) {
        return `${trimmed} ${suggestion}`;
      }
      return `${trimmed}. ${suggestion}`;
    });
  };

  const load = useCallback(() => {
    api
      .get('/forms')
      .then((res) => setItems(res.data))
      .catch(() => setItems([]));
  }, []);

  const openAiModal = () => {
    aiDocument.reset();
    setAiPrompt('');
    setAiResult(null);
    setAiError('');
    setAiLoading(false);
    setShowPromptSuggestions(false);
    setSelectedGeneratedFieldIndexes([]);
    setAiModalOpen(true);
  };

  const closeAiModal = () => {
    aiDocument.reset();
    setAiModalOpen(false);
    setAiLoading(false);
    setAiResult(null);
    setAiError('');
    setSelectedGeneratedFieldIndexes([]);
  };

  const handleGenerateFormAi = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt && !aiDocument.file) {
      setAiError('Descreva o formulário desejado ou anexe um modelo.');
      return;
    }
    const operation = aiDocument.start();
    setAiLoading(true);
    setAiError('');
    setAiResult(null);
    try {
      const request = buildFormAiRequest(prompt, aiDocument.file);
      const response = await api.post(request.url, request.data, { signal: operation.signal });
      if (!aiDocument.isCurrent(operation)) return;
      const result = response.data?.form;
      const generated = result ? { ...result, schema: normalizeFormAiSuggestion(result.schema) } : null;
      if (!generated) {
        setAiError('A resposta recebida não pôde ser interpretada. Tente detalhar um pouco mais.');
        setAiResult(null);
        return;
      }
      setAiResult(generated);
      const generatedFields = Array.isArray(generated?.schema?.fields) ? generated.schema.fields : [];
      setSelectedGeneratedFieldIndexes(generatedFields.map((_, index) => index));
    } catch (error) {
      if (!aiDocument.isCurrent(operation)) return;
      const message = error?.response?.data?.error || error?.message || 'Falha ao gerar sugestão de formulário.';
      setAiError(message);
      setAiResult(null);
    } finally {
      if (aiDocument.isCurrent(operation)) setAiLoading(false);
    }
  };

  const handleUseGeneratedForm = () => {
    if (!aiResult || !openTab) return;
    const generatedFields = Array.isArray(aiResult?.schema?.fields) ? aiResult.schema.fields : [];
    const selectedFields = generatedFields.filter((_, index) => selectedGeneratedFieldIndexes.includes(index));
    if (generatedFields.length > 0 && selectedFields.length === 0) {
      setAiError('Selecione ao menos um campo para continuar.');
      return;
    }
    const selectedGroupIds = new Set(
      selectedFields
        .map((field) => field?.groupId)
        .filter((groupId) => typeof groupId === 'string' && groupId.trim()),
    );
    const generatedGroups = Array.isArray(aiResult?.schema?.groups) ? aiResult.schema.groups : [];
    const filteredGroups =
      selectedGroupIds.size > 0
        ? generatedGroups.filter((group) => selectedGroupIds.has(group?.id))
        : generatedGroups;
    const filteredSuggestion = {
      ...aiResult,
      schema: {
        ...(aiResult.schema || {}),
        groups: filteredGroups,
        fields: selectedFields,
      },
    };
    const tabId = `form-ai-${Date.now()}`;
    const tabLabel = aiResult.name ? `IA: ${aiResult.name}` : 'Formulário sugerido';
    openTab(tabId, tabLabel, FormBuilder, {
      onSaved: load,
      openTab,
      returnToTabId: 'forms',
      initialSuggestion: filteredSuggestion,
    });
    closeAiModal();
  };

  const toggleGeneratedFieldSelection = (index) => {
    setSelectedGeneratedFieldIndexes((current) =>
      current.includes(index) ? current.filter((item) => item !== index) : [...current, index],
    );
  };

  useEffect(() => {
    load();
  }, [load]);

  const openNew = useCallback(() => {
    if (!canCreate) return;
    if (openTab) {
      openTab('form-new', 'Novo Formulário', FormBuilder, { onSaved: load, openTab, returnToTabId: 'forms' });
    }
  }, [canCreate, load, openTab]);

  const assistantActionType = assistantAction?.type;
  const assistantActionKey = assistantAction?.key;

  useEffect(() => {
    if (assistantActionType !== 'new-form') {
      return;
    }
    if (!canCreate) {
      window.alert('Seu usuário não possui permissão para criar formulários.');
      return;
    }
    openNew();
  }, [assistantActionKey, assistantActionType, canCreate, openNew]);

  const filteredItems = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    const start = parseDate(startDate);
    const end = parseDate(endDate, true);

    return items.filter((form) => {
      const hasDescription = Boolean(form.description && form.description.trim());
      const matchesDescription =
        descriptionFilter === 'all' ||
        (descriptionFilter === 'with' && hasDescription) ||
        (descriptionFilter === 'without' && !hasDescription);

      const createdAt = form.createdAt ? new Date(form.createdAt) : null;
      const matchesDates =
        (!start || (createdAt && createdAt >= start)) &&
        (!end || (createdAt && createdAt <= end));

      const matchesSearch =
        !normalizedTerm ||
        form.name.toLowerCase().includes(normalizedTerm) ||
        (form.description ? form.description.toLowerCase().includes(normalizedTerm) : false) ||
        (createdAt ? createdAt.toLocaleDateString('pt-BR').includes(normalizedTerm) : false);

      return matchesDescription && matchesSearch && matchesDates;
    });
  }, [descriptionFilter, endDate, items, searchTerm, startDate]);

  const openEdit = (f) => {
    if (!canEdit) return;
    if (openTab) {
      openTab(`form-${f.id}`, `Editar: ${f.name}`, FormBuilder, {
        formId: f.id,
        onSaved: load,
        openTab,
        returnToTabId: 'forms',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;
    if (window.confirm('Remover formulário?')) {
      try {
        await api.delete(`/forms/${id}`);
        load();
      } catch (err) {
        const msg = err.response?.data?.error || 'Erro ao remover formulário';
        alert(msg);
      }
    }
  };

  const triggerJsonDownload = (payload, fileName) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const safeExportFileName = (name) => {
    const base = String(name || 'formulario')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
    return `${base || 'formulario'}.json`;
  };

  const handleExport = async (form) => {
    if (!form?.id) return;
    try {
      const response = await api.get(`/forms/${form.id}/export`);
      triggerJsonDownload(response.data, safeExportFileName(form.name));
    } catch (error) {
      const message = error?.response?.data?.error || 'Erro ao exportar formulário';
      alert(message);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canCreate) return;
    setImportingForm(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const result = await api.post('/forms/import', { payload });
      load();
      const created = result?.data;
      if (created?.id && openTab) {
        openTab(`form-${created.id}`, `Editar: ${created.name}`, FormBuilder, {
          formId: created.id,
          onSaved: load,
          openTab,
          returnToTabId: 'forms',
        });
      }
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Erro ao importar formulário';
      alert(message === 'Unexpected token' ? 'Arquivo JSON inválido.' : message);
    } finally {
      setImportingForm(false);
    }
  };

  const handleDuplicate = async (form) => {
    if (!canCreate || !form) return;
    const suggestion = form.name ? `${form.name} (cópia)` : 'Formulário copiado';
    let chosenName = suggestion;

    if (typeof window !== 'undefined' && window.prompt) {
      const response = window.prompt('Como deseja nomear a cópia do formulário?', suggestion);
      if (response === null) {
        return;
      }
      chosenName = response.trim();
      if (!chosenName) {
        alert('Informe um nome válido para a cópia do formulário.');
        return;
      }
    }

    try {
      const result = await api.post(`/forms/${form.id}/copy`, { name: chosenName });
      load();
      const created = result?.data;
      if (created?.id && openTab) {
        openTab(`form-${created.id}`, `Editar: ${created.name}`, FormBuilder, {
          formId: created.id,
          onSaved: load,
          openTab,
          returnToTabId: 'forms',
        });
      }
    } catch (error) {
      const message = error?.response?.data?.error || 'Erro ao copiar formulário';
      alert(message);
    }
  };

  const hasActiveFilters = Boolean(
    searchTerm ||
      descriptionFilter !== 'all' ||
      startDate ||
      endDate
  );

  const buildExternalLink = (token) => {
    if (!token) return '';
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/formularios/${token}`;
    }
    return `/formularios/${token}`;
  };

  const handleCopyExternalLink = async (form) => {
    if (!form?.externalAccessToken) return;
    const link = buildExternalLink(form.externalAccessToken);
    const finalizeCopy = () => {
      setCopiedFormId(form.id);
      setTimeout(() => setCopiedFormId(null), 2000);
    };

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        finalizeCopy();
        return;
      }
      throw new Error('Clipboard API indisponível');
    } catch (error) {
      if (typeof window !== 'undefined') {
        window.prompt('Copie o link externo:', link);
      }
      finalizeCopy();
    }
  };

  const handleOpenQrCode = (form) => {
    if (!form?.externalAccessToken) return;
    setQrForm(form);
  };

  const handleCloseQrCode = () => setQrForm(null);

  const toggleExternalLinkVisibility = (formId) => {
    setExpandedExternalLinks((current) => ({
      ...current,
      [formId]: !current[formId],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 rounded-3xl border border-slate-200/70 bg-blue-600/20 p-6 shadow-lg sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Formulários</h1>
          <p className="mt-1 text-sm text-slate-600">
            Crie e mantenha os formulários utilizados nos processos da sua equipe.
          </p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canEdit && <button type="button" onClick={() => setCustomerImportOpen(true)}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-50">
              <FaAddressBook/> Importar para Clientes
            </button>}
            <button
              onClick={openAiModal}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-purple-200 bg-white px-5 py-2 text-sm font-semibold text-purple-600 shadow-md transition hover:border-purple-300 hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
            >
              <FaMagic className="text-xs" />
              Assistente IA
            </button>
            <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-white px-5 py-2 text-sm font-semibold text-blue-600 shadow-md transition hover:border-blue-300 hover:bg-blue-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-400">
              <FaFileImport className="text-xs" />
              {importingForm ? 'Importando...' : 'Importar'}
              <input
                type="file"
                accept="application/json,.json"
                onChange={handleImportFile}
                disabled={importingForm}
                className="sr-only"
              />
            </label>
            <button
              onClick={openNew}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-sky-500 px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:from-blue-500 hover:to-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              <FaPlus className="text-xs" />
              Novo formulário
            </button>
          </div>
        )}
      </div>

      <Modal open={customerImportOpen} onClose={() => setCustomerImportOpen(false)} size="max-w-5xl">
        <CustomerFormImport forms={items} onClose={() => setCustomerImportOpen(false)} />
      </Modal>

      <Modal open={aiModalOpen} onClose={closeAiModal}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Assistente inteligente de formulários</h2>
          <p className="text-sm text-slate-600">
            Anexe um modelo ou descreva os campos e o objetivo do formulário. A IA prepara uma estrutura inicial que você pode ajustar no construtor.
          </p>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700" htmlFor="ai-form-prompt">
              Instruções (opcionais com documento)
            </label>
            <textarea
              id="ai-form-prompt"
              value={aiPrompt}
              onChange={(event) => setAiPrompt(event.target.value)}
              rows={4}
              placeholder="Ex.: Formulário de solicitação de reembolso com dados do colaborador, valores e anexos de comprovantes."
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
            />
            {quickPromptSuggestions.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPromptSuggestions((current) => !current)}
                className="inline-flex items-center rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
              >
                {showPromptSuggestions ? 'Ocultar sugestões' : 'Ver sugestões'}
              </button>
            )}
            {showPromptSuggestions && quickPromptSuggestions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Sugestões rápidas
                </p>
                <div className="flex flex-wrap gap-2">
                  {quickPromptSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => applyPromptSuggestion(suggestion)}
                      className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <FormAiDocumentAttachment file={aiDocument.file} loading={aiLoading} onChange={(file) => {
            const error = aiDocument.choose(file);
            setAiLoading(false);
            setAiResult(null);
            setSelectedGeneratedFieldIndexes([]);
            setAiError(error);
          }} />
          {aiError && <p role="alert" className="text-sm text-red-600">{aiError}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={closeAiModal}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Fechar
          </button>
          <button
            onClick={handleGenerateFormAi}
            disabled={aiLoading}
            className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaMagic className="text-xs" />
            {aiLoading ? 'Gerando...' : 'Gerar formulário'}
          </button>
        </div>
        {aiResult && (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/70">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Estrutura sugerida</h3>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{aiResult.summary}</p>
            </div>
            {Array.isArray(aiResult.keywords) && aiResult.keywords.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300">Palavras-chave</span>
                <div className="flex flex-wrap gap-2">
                  {aiResult.keywords.map((keyword) => (
                    <span
                      key={keyword}
                      className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700 dark:bg-purple-500/25 dark:text-purple-100 dark:ring-1 dark:ring-purple-300/30"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="grid gap-3 rounded-xl border border-slate-200 bg-white/90 p-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200 sm:grid-cols-2">
              {(aiResult.schema?.groups || []).map((group) => (
                <div key={group.id} className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">Grupo</span>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{group.label}</p>
                </div>
              ))}
            </div>
            {Array.isArray(aiResult.schema?.fields) && aiResult.schema.fields.length > 0 && (
              <div className="space-y-2 rounded-xl border border-slate-200 bg-white/90 p-3 text-sm text-slate-700">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Campos gerados</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedGeneratedFieldIndexes(aiResult.schema.fields.map((_, index) => index))}
                      className="text-xs font-semibold text-purple-600 transition hover:text-purple-700"
                    >
                      Selecionar todos
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedGeneratedFieldIndexes([])}
                      className="text-xs font-semibold text-slate-500 transition hover:text-slate-700"
                    >
                      Limpar
                    </button>
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {aiResult.schema.fields.map((field, index) => (
                    <label
                      key={field.id || `generated-field-${index}`}
                      className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 px-2 py-2 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedGeneratedFieldIndexes.includes(index)}
                        onChange={() => toggleGeneratedFieldSelection(index)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span>
                        <span className="block font-medium text-slate-900">{field.label}</span>
                        <span className="text-xs text-slate-500">Tipo: {field.type}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(aiResult.suggestions) && aiResult.suggestions.length > 0 && (
              <div className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300">Sugestões</span>
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
                  {aiResult.suggestions.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={handleUseGeneratedForm}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-700"
              >
                <FaFileAlt className="text-xs" />
                Abrir no construtor
              </button>
            </div>
          </div>
        )}
      </Modal>

      <QrCodeModal
        open={Boolean(qrForm)}
        onClose={handleCloseQrCode}
        link={qrForm ? buildExternalLink(qrForm.externalAccessToken) : ''}
        formName={qrForm?.name}
        onCopyLink={qrForm ? () => handleCopyExternalLink(qrForm) : null}
      />

      <div className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl backdrop-blur">
        {items.length ? (
          filteredItems.length ? (
            <>
              <div className="mb-4 space-y-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="relative w-full md:max-w-sm">
                    <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Buscar formulários por nome, descrição ou data"
                      className="w-full rounded-full border border-slate-200 bg-white py-2 pl-11 pr-4 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.25em] text-slate-500">
                      <FaFilter className="text-slate-400" /> filtros
                    </div>
                    <select
                      value={descriptionFilter}
                      onChange={(event) => setDescriptionFilter(event.target.value)}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="all">Todos os formulários</option>
                      <option value="with">Com descrição</option>
                      <option value="without">Sem descrição</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                      <FaCalendarAlt className="text-slate-400" /> datas
                    </div>
                    <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:max-w-xs"
                      />
                      <span className="hidden text-xs font-medium uppercase tracking-[0.3em] text-slate-400 sm:block">
                        até
                      </span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:max-w-xs"
                      />
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setDescriptionFilter('all');
                        setStartDate('');
                        setEndDate('');
                      }}
                      className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                    >
                      Limpar filtros
                    </button>
                  )}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {filteredItems.map((f) => {
                  const externalLink = buildExternalLink(f.externalAccessToken);
                  const isExternalLinkVisible = Boolean(expandedExternalLinks[f.id]);
                  return (
                    <div
                      key={f.id}
                      className="group relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl border border-slate-200/70 bg-white/95 p-5 shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
                    >
                      <div className="absolute right-5 top-5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-500">
                        #{f.id}
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-sky-500 text-white shadow-inner">
                          <FaFileAlt />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-lg font-semibold text-slate-900">{f.name}</h2>
                          {f.description && (
                            <p className="mt-1 text-sm text-slate-500">{f.description}</p>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                              <FaCalendarAlt className="text-slate-400" />
                              Criado em {formatDate(f.createdAt)}
                            </span>
                          </div>
                          {f.externalAccessEnabled && (
                            <div className="mt-3 rounded-2xl border border-green-200 bg-green-50/70 p-3 text-sm text-green-700">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="inline-flex items-center gap-2 font-semibold">
                                  <FaGlobe className="text-green-600" />
                                  Link externo ativo
                                </div>
                                <button
                                  type="button"
                                  onClick={() => toggleExternalLinkVisibility(f.id)}
                                  className="inline-flex items-center gap-2 self-start rounded-full border border-green-300 bg-white/70 px-3 py-1 text-xs font-semibold text-green-700 transition hover:border-green-400 hover:bg-white"
                                >
                                  {isExternalLinkVisible ? 'Ocultar link' : 'Mostrar link'}
                                </button>
                              </div>
                              {isExternalLinkVisible && (
                                externalLink ? (
                                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <a
                                      href={externalLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="break-all rounded-xl bg-white/70 px-3 py-2 text-xs font-medium text-green-700 shadow-inner transition hover:bg-white"
                                    >
                                      {externalLink}
                                    </a>
                                    <button
                                      type="button"
                                      onClick={() => handleCopyExternalLink(f)}
                                      className="inline-flex items-center gap-2 rounded-xl border border-green-300 bg-green-100 px-3 py-2 text-xs font-semibold text-green-700 transition hover:border-green-400 hover:bg-green-200"
                                    >
                                      <FaCopy />
                                      {copiedFormId === f.id ? 'Link copiado!' : 'Copiar link'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleOpenQrCode(f)}
                                      className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                                    >
                                      <FaQrcode />
                                      QR Code
                                    </button>
                                  </div>
                                ) : (
                                  <p className="mt-2 text-xs text-green-600">Link externo disponível em breve.</p>
                                )
                              )}
                            </div>
                          )}

                        </div>
                      </div>
                      <div className="mt-auto flex min-h-9 items-center justify-end gap-2">
                        <FormActionButton
                          icon={FaDownload}
                          label="Exportar"
                          onClick={() => handleExport(f)}
                          className="border-emerald-200 bg-emerald-50/70 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100"
                        />
                        {canCreate && (
                          <FormActionButton
                            icon={FaClone}
                            label="Copiar"
                            onClick={() => handleDuplicate(f)}
                            className="border-slate-200 bg-slate-50/70 text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                          />
                        )}
                        {canEdit && (
                          <FormActionButton
                            icon={FaRegEdit}
                            label="Editar"
                            onClick={() => openEdit(f)}
                            className="border-blue-200 bg-blue-50/70 text-blue-600 hover:border-blue-300 hover:bg-blue-100"
                          />
                        )}
                        {canDelete && (
                          <FormActionButton
                            icon={FaTrashAlt}
                            label="Remover"
                            onClick={() => handleDelete(f.id)}
                            className="border-red-200 bg-red-50/70 text-red-600 hover:border-red-300 hover:bg-red-100"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-200/80 bg-slate-50/70 p-10 text-center text-slate-500">
              <FaFileAlt className="text-4xl text-slate-300" />
              <p className="text-sm font-medium text-slate-600">
                Nenhum formulário corresponde à pesquisa ou filtro selecionado.
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setDescriptionFilter("all");
                }}
                className="inline-flex items-center gap-2 rounded-2xl border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition hover:border-blue-400 hover:bg-blue-100"
              >
                Limpar filtros
              </button>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-200/80 bg-slate-50/70 p-10 text-center text-slate-500">
            <FaFileAlt className="text-4xl text-slate-300" />
            <p className="text-sm font-medium text-slate-600">
              Nenhum formulário cadastrado no momento.
            </p>
            <button
              onClick={openNew}
              className="inline-flex items-center gap-2 rounded-2xl border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition hover:border-blue-400 hover:bg-blue-100"
            >
              <FaPlus className="text-xs" />
              Criar primeiro formulário
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
